/**
 * My Boards — index page listing every custom board in the workspace.
 *
 * All boards are visible to everyone. Only the creator or an admin can
 * open board settings & delete, but anyone can open + add cards.
 *
 * Search: instant, board-name only, case-insensitive.
 * Favourites: per-user ⭐ pin. Starred boards float to the top of the grid.
 */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, Plus, Layers, Loader2, Users, Trash2, Search, Star, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { formatApiError } from '@/utils/apiError';
import NewBoardModal from '@/components/boards/NewBoardModal';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB') : '—');

export default function CustomBoards() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const homePath = ['admin', 'manager', 'team'].includes(user?.role) ? '/staff' : '/';
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/boards`, { credentials: 'include' });
      if (!r.ok) throw new Error(formatApiError(await r.json().catch(() => ({}))) || 'Failed');
      setBoards(await r.json());
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  const canDelete = (board) =>
    board.created_by === user?.user_id
    || ['admin', 'manager'].includes(user?.role);

  const removeBoard = async (board, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${board.name}"? All its cards go too.`)) return;
    try {
      const r = await fetch(`${API_URL}/api/boards/${board.id}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error(formatApiError(await r.json().catch(() => ({}))) || 'Failed');
      toast.success(`${board.name} deleted`);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const toggleFavourite = async (board, e) => {
    e.stopPropagation();
    const next = !board.is_favourite;
    // Optimistic update
    setBoards((prev) => prev.map((b) => (b.id === board.id ? { ...b, is_favourite: next } : b)));
    try {
      const r = await fetch(`${API_URL}/api/boards/${board.id}/favourite`, {
        method: next ? 'POST' : 'DELETE',
        credentials: 'include',
      });
      if (!r.ok) throw new Error(formatApiError(await r.json().catch(() => ({}))) || 'Failed');
    } catch (err) {
      // Revert on failure
      setBoards((prev) => prev.map((b) => (b.id === board.id ? { ...b, is_favourite: !next } : b)));
      toast.error(err.message);
    }
  };

  // Filter by name (case-insensitive) then sort: favourites first, then by updated_at desc
  const sortedBoards = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? boards.filter((b) => (b.name || '').toLowerCase().includes(q))
      : boards;
    return [...filtered].sort((a, b) => {
      if (!!a.is_favourite !== !!b.is_favourite) return a.is_favourite ? -1 : 1;
      const at = a.updated_at ? Date.parse(a.updated_at) : 0;
      const bt = b.updated_at ? Date.parse(b.updated_at) : 0;
      return bt - at;
    });
  }, [boards, query]);

  const favCount = boards.filter((b) => b.is_favourite).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="text-white" style={{ background: 'linear-gradient(135deg, #0f172a, #1e293b)' }}>
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3 flex-wrap">
          <button onClick={() => navigate(homePath)} data-testid="boards-back" className="p-2 hover:bg-white/10 rounded-lg shrink-0" aria-label="Home">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-xl font-bold flex items-center gap-2 truncate">
              <Layers className="w-5 h-5 shrink-0" /> My Boards
            </h1>
            <p className="hidden sm:block text-white/70 text-xs">
              Build your own Kanban with stages that make sense for you · everyone in Ox can see and add to any board
            </p>
          </div>
          <Button onClick={() => setShowNew(true)} size="sm" className="bg-white text-slate-900 hover:bg-slate-100 shrink-0" data-testid="boards-new">
            <Plus className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">New Board</span>
          </Button>
          <Button onClick={() => navigate(homePath)} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0">
            <Home className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Home</span>
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Search bar — always visible so busy staff can jump straight to a board */}
        {!loading && boards.length > 0 && (
          <div className="mb-4 flex items-center gap-2">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <Input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search boards by name…"
                className="pl-9 pr-9 h-10 bg-white"
                data-testid="boards-search-input"
                aria-label="Search boards by name"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700 rounded"
                  data-testid="boards-search-clear"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {favCount > 0 && (
              <span
                className="hidden sm:inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1"
                data-testid="boards-fav-count"
              >
                <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
                {favCount} pinned
              </span>
            )}
          </div>
        )}

        {loading ? (
          <Card className="p-10 text-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></Card>
        ) : boards.length === 0 ? (
          <Card className="p-12 text-center" data-testid="boards-empty">
            <Layers className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <h3 className="font-bold text-slate-900 mb-1">No boards yet</h3>
            <p className="text-slate-500 text-sm mb-4">Spin one up — pick a template or start blank.</p>
            <Button onClick={() => setShowNew(true)} className="bg-slate-900 hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-1" /> New Board
            </Button>
          </Card>
        ) : sortedBoards.length === 0 ? (
          <Card className="p-10 text-center" data-testid="boards-search-empty">
            <Search className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <h3 className="font-bold text-slate-900 mb-1">No boards match "{query}"</h3>
            <p className="text-slate-500 text-sm">Try a different word, or clear the search.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {sortedBoards.map((b) => (
              <Card
                key={b.id}
                onClick={() => navigate(`/boards/${b.id}`)}
                className="group p-4 cursor-pointer hover:shadow-md transition relative overflow-hidden"
                data-testid={`board-card-${b.id}`}
              >
                <div className="absolute top-0 left-0 right-0 h-1" style={{ background: b.colour || '#6366F1' }} />
                <div className="flex items-start justify-between gap-2 mt-1">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 truncate flex items-center gap-1.5">
                      {b.is_favourite && (
                        <Star className="w-3.5 h-3.5 shrink-0 fill-amber-400 text-amber-400" aria-label="Pinned" />
                      )}
                      <span className="truncate">{b.name}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 font-mono truncate">{b.reference}</div>
                  </div>
                  <div className="flex items-center gap-0.5 shrink-0">
                    <button
                      type="button"
                      onClick={(e) => toggleFavourite(b, e)}
                      className={`p-1 rounded transition ${b.is_favourite ? 'text-amber-500 hover:text-amber-600' : 'text-slate-300 hover:text-amber-500 opacity-70 group-hover:opacity-100 focus:opacity-100'}`}
                      data-testid={`board-fav-${b.id}`}
                      title={b.is_favourite ? 'Unpin from favourites' : 'Pin to favourites'}
                      aria-pressed={!!b.is_favourite}
                    >
                      <Star className={`w-4 h-4 ${b.is_favourite ? 'fill-current' : ''}`} />
                    </button>
                    {canDelete(b) && (
                      <button
                        type="button"
                        onClick={(e) => removeBoard(b, e)}
                        className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-slate-400 hover:text-rose-600 p-1 -mr-1 rounded transition"
                        data-testid={`board-delete-${b.id}`}
                        title="Delete board"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                {b.description && (
                  <p className="text-xs text-slate-600 mt-1 line-clamp-2">{b.description}</p>
                )}
                <div className="mt-3 flex items-center gap-3 text-[11px] text-slate-500">
                  <span className="inline-flex items-center gap-1">
                    <Users className="w-3 h-3" /> {b.card_count || 0} card{b.card_count === 1 ? '' : 's'}
                  </span>
                  <span>·</span>
                  <span>{(b.columns || []).length} columns</span>
                  <span className="ml-auto">Updated {fmtDate(b.updated_at)}</span>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {showNew && (
        <NewBoardModal
          onClose={() => setShowNew(false)}
          onCreated={(board) => { setShowNew(false); navigate(`/boards/${board.id}`); }}
        />
      )}
    </div>
  );
}
